/**
 * Segmented — a segmented control / toggle group (Claude Design `.seg`).
 *
 * A bordered `surface-2` track of buttons; the selected segment lifts to
 * `surface-3` with a small shadow. Controlled via `value` / `onValueChange`.
 * Exposed as a `radiogroup` (each option a `radio`) for accessibility.
 *
 *   <Segmented
 *     value={view}
 *     onValueChange={setView}
 *     options={[
 *       { value: 'list', label: 'List' },
 *       { value: 'board', label: 'Board' },
 *     ]}
 *   />
 */
import type { ReactElement, ReactNode } from 'react';

import { cn } from './cn.js';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: ReactNode;
  /** Disable this single segment. */
  disabled?: boolean;
  /** Accessible label when `label` is an icon-only node. */
  'aria-label'?: string;
}

export interface SegmentedProps<T extends string = string> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onValueChange: (value: T) => void;
  /** Accessible group label. */
  'aria-label'?: string;
  className?: string;
}

export function Segmented<T extends string = string>({
  options,
  value,
  onValueChange,
  'aria-label': ariaLabel,
  className,
}: SegmentedProps<T>): ReactElement {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex gap-0.5 rounded-md border border-border bg-surface-2 p-[3px]',
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={opt['aria-label']}
            disabled={opt.disabled}
            data-state={selected ? 'on' : 'off'}
            onClick={() => onValueChange(opt.value)}
            className={cn(
              'whitespace-nowrap rounded-sm px-[11px] py-[5px] text-[12.5px] font-semibold transition [&_svg]:size-[15px]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'bg-surface-3 text-text shadow-sm'
                : 'text-muted hover:text-text',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
